import { useState, useEffect, useRef, useCallback } from 'react';
import { navigate } from 'gatsby';
import { normalizePath, getPageTitle } from '../config/pageTitles';
import { getUsername } from '../components/AuthWrapper';

// Сколько скрытых вкладок держим смонтированными (keep-alive). Старые уходят
// «в холод»: чип остаётся, но страница размонтируется и перезагрузится по клику.
const MAX_ALIVE = 8;

const storageKey = () => `site-tabs:${getUsername() || 'anon'}`;

// Вся логика вкладок сайта. Идентичность вкладки — pathname (без слэша в конце);
// query-переходы внутри той же страницы (/orders?tab=…) обновляют url вкладки,
// а не создают новую.
const useSiteTabs = (location) => {
  const path = normalizePath(location?.pathname);
  const url = path + (location?.search || '');

  // Детерминированный seed из location — первый клиентский рендер совпадает с SSR.
  const [tabs, setTabs] = useState(() => [{ path, url, title: getPageTitle(path) }]);
  const [alivePaths, setAlivePaths] = useState(() => [path]);

  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const pathRef = useRef(path);
  const aliveRef = useRef([path]); // LRU: свежие в начале
  const scrollMapRef = useRef(new Map()); // path -> scrollY
  const restoredRef = useRef(false);
  const prevRenderPathRef = useRef(path);
  const pendingScrollRef = useRef(null);

  // Скролл фиксируем в фазе рендера: старая страница ещё в DOM и scrollY валиден.
  // После коммита браузер обрезает scrollY под высоту новой страницы — поздно.
  // Цель восстановления тоже читаем здесь, до того как clamp-события испортят карту.
  if (typeof window !== 'undefined' && prevRenderPathRef.current !== path) {
    scrollMapRef.current.set(prevRenderPathRef.current, window.scrollY);
    pendingScrollRef.current = scrollMapRef.current.get(path) ?? 0;
    prevRenderPathRef.current = path;
  }

  // Восстановление списка вкладок из localStorage (один раз, после маунта).
  // Держим этот эффект ПЕРВЫМ: он должен прочитать сохранённое до записи persist-эффекта.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey());
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && Array.isArray(saved.tabs)) {
          const seen = new Set();
          const merged = [];
          for (const t of saved.tabs) {
            if (!t || typeof t.path !== 'string' || typeof t.url !== 'string') continue;
            const p = normalizePath(t.path);
            if (seen.has(p)) continue;
            seen.add(p);
            // заголовок всегда из конфига: сохранённый в localStorage мог устареть (вкладка открыта до появления названия)
            merged.push(p === path ? { path: p, url, title: getPageTitle(p) } : { path: p, url: t.url, title: getPageTitle(p) });
          }
          if (!seen.has(path)) merged.push({ path, url, title: getPageTitle(path) });
          if (merged.length) setTabs(merged);
        }
      }
    } catch (e) {
      // битое состояние — начинаем с текущей страницы
    }
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Сохранение (после восстановления, чтобы не затереть список seed-вкладкой).
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem(storageKey(), JSON.stringify({ v: 1, tabs, activePath: path }));
    } catch (e) { /* quota — не критично */ }
  }, [tabs, path]);

  // Навигация: upsert вкладки + LRU keep-alive.
  useEffect(() => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.path === path);
      if (idx === -1) return [...prev, { path, url, title: getPageTitle(path) }];
      if (prev[idx].url === url) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], url };
      return next;
    });
    const lru = [path, ...aliveRef.current.filter((p) => p !== path)].slice(0, MAX_ALIVE);
    aliveRef.current = lru;
    setAlivePaths(lru);
  }, [path, url]);

  // Смена вкладки: восстановить скролл + толкнуть resize (chart.js в display:none
  // рисуется в нулевую ширину — после показа нужен пинок).
  useEffect(() => {
    const prevPath = pathRef.current;
    pathRef.current = path;
    if (prevPath === path) return; // смена только query — скролл не трогаем
    const target = pendingScrollRef.current ?? 0;
    pendingScrollRef.current = null;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.scrollTo(0, target);
      window.dispatchEvent(new Event('resize'));
    }));
  }, [path]);

  const activateTab = useCallback((tab) => {
    if (normalizePath(tab.path) === pathRef.current) return;
    navigate(tab.url || tab.path);
  }, []);

  const closeTab = useCallback((closePath) => {
    const p = normalizePath(closePath);
    const prev = tabsRef.current;
    const idx = prev.findIndex((t) => t.path === p);
    if (idx === -1) return;

    scrollMapRef.current.delete(p);
    aliveRef.current = aliveRef.current.filter((x) => x !== p);
    setAlivePaths([...aliveRef.current]);

    const next = prev.filter((t) => t.path !== p);
    if (next.length === 0) {
      setTabs([{ path: '/', url: '/', title: getPageTitle('/') }]);
      if (pathRef.current !== '/') navigate('/');
      return;
    }
    setTabs(next);
    if (p === pathRef.current) {
      const neighbor = next[Math.min(idx, next.length - 1)];
      navigate(neighbor.url);
    }
  }, []);

  return { tabs, activePath: path, alivePaths, activateTab, closeTab };
};

export default useSiteTabs;
